/**
 * One client's workspace.
 *
 * The client id lives in the URL rather than in global state, which makes the
 * current workspace bookmarkable, survives a reload, and matches the API,
 * where every scoped route is nested under /api/clients/:clientId. There is no
 * hidden "currently selected client" to fall out of sync.
 */

import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell.tsx';
import ClientSwitcher, { type ClientOption } from '../components/ClientSwitcher.tsx';
import { useSession } from '../lib/auth.ts';
import Events from './Events.tsx';
import EventDetail from './EventDetail.tsx';
import Dashboard from './Dashboard.tsx';
import SponsorDetail from './SponsorDetail.tsx';
import Library from './Library.tsx';
import LibraryDetail from './LibraryDetail.tsx';

/**
 * What the left of the topbar says on each screen.
 *
 * THE BAR NEEDS BOTH HALVES. When the client switcher moved to the right, this
 * side was left empty and the whole bar rendered as a blank white strip —
 * reported on the sponsor review screen, true on every screen in the
 * workspace. The prototype has never had an empty left: it reads
 * "<b>IBBA</b> · sponsor logo collection", the name saying WHERE YOU ARE and
 * the button on the right being the control that changes it.
 *
 * Deliberately the section rather than the record. Workspace routes by view and
 * does not know the event or sponsor being looked at — those are loaded by the
 * screens themselves — so naming them here would mean plumbing titles back up
 * for a line of chrome. Each of those screens already carries its own heading
 * and a link back to its parent.
 */
const SECTION: Record<string, string> = {
  dashboard: 'sponsor logo collection',
  events: 'events and deadlines',
  event: 'event setup and sponsors',
  sponsor: 'one sponsor and their logos',
  library: 'approved sponsor logos',
  'library-detail': 'one company in the library',
};

export default function Workspace({ view = 'dashboard' }: { view?: 'dashboard' | 'events' | 'event' | 'sponsor' | 'library' | 'library-detail' }) {
  const { clientId = '' } = useParams();
  const { data: session } = useSession();
  const isPlatformAdmin = (session?.user as { role?: string } | undefined)?.role === 'fps_super_admin';

  const [clients, setClients] = useState<ClientOption[] | null>(null);

  useEffect(() => {
    void fetch('/api/clients')
      .then((r) => (r.ok ? r.json() : { clients: [] }))
      .then((b: { clients: ClientOption[] }) => setClients(b.clients))
      .catch(() => setClients([]));
  }, []);

  if (!clients) return <div className="app-loading" aria-busy="true" />;

  /* The API answers 404 for a client you cannot reach, so this only avoids
   * rendering a shell around an inevitable error. It is not the access check —
   * that lives server-side, on every request. */
  if (clients.length > 0 && !clients.some((c) => c.id === clientId)) {
    return <Navigate to="/" replace />;
  }

  const current = clients.find((c) => c.id === clientId);

  return (
    <AppShell
      active={
        view === 'dashboard'
          ? 'dashboard'
          : view === 'library' || view === 'library-detail'
            ? 'library'
            : 'events'
      }
      clientId={clientId}
      context={
        current && (
          <>
            <b>{current.name}</b> · {SECTION[view] ?? 'sponsor logo collection'}
          </>
        )
      }
      /* Handed over rather than re-fetched: this component already loads them
         to check the id in the URL is one this user can reach. */
      clients={clients}
      /*
       * THE SWITCHER IS NOW A FALLBACK, not the way in.
       *
       * The rail in the sidebar carries the clients on every screen, including
       * the ones this component does not wrap. It only appears where there is
       * more than one client, so a client admin with a single organization
       * still gets their name in the topbar — otherwise the bar would be empty
       * and they would have no idea which workspace they were in.
       */
      switcher={
        clients.length > 1 ? undefined : (
          <ClientSwitcher clients={clients} currentId={clientId} showAllOption={isPlatformAdmin} />
        )
      }
    >
      {!current ? (
        <div className="app-loading" aria-busy="true" />
      ) : view === 'library-detail' ? (
        <LibraryDetail />
      ) : view === 'library' ? (
        <Library />
      ) : view === 'sponsor' ? (
        <SponsorDetail />
      ) : view === 'event' ? (
        <EventDetail />
      ) : view === 'events' ? (
        <Events />
      ) : (
        <Dashboard />
      )}
    </AppShell>
  );
}
